"use client";

import React, { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const stats = [{
  value: "70",
  label: "Years Combined Experience"
}, {
  value: "3",
  label: "Core Global Regions"
}, {
  value: "100s",
  label: "Lives Changed Forever"
}];
export default function GlobalSnapshots() {
  return <section data-section-id="126757" id="global-expertise-in-numbers" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48">
            {}
            <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/dark_world_map_silhouette.png)] bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none" aria-hidden="true" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-20 md:gap-8">
                    {stats.map((stat, i) => <StatItem key={i} stat={stat} index={i} />)}
                </div>
            </div>
        </section>;
}
function StatItem({
  stat,
  index
}: {
  stat: {
    value: string;
    label: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <div ref={ref} data-index={index} className="flex justify-center md:justify-end first:md:justify-start [&:nth-child(2)]:md:justify-center">
            <div className="flex flex-col items-end">
                <div className="relative overflow-hidden px-2 py-1">
                    {}
                    <span className="font-default text-6xl md:text-7xl leading-[0.8] text-secondary block tracking-tighter">
                        {stat.value}
                    </span>
                    
                    {}
                    <div className={`absolute top-0 -left-[100%] h-full w-full bg-secondary/90 skew-x-12 transform transition-transform duration-1000 ease-in-out mix-blend-difference ${isVisible ? 'translate-x-[300%]' : 'translate-x-0'}`} style={{
          transitionDelay: `${index * 200}ms`
        }} aria-hidden="true" />
                </div>
                
                {}
                <span className="font-mono text-xs text-primary uppercase tracking-widest mt-4 text-right pr-2">
                    {stat.label}
                </span>
            </div>
        </div>;
}